import { BrowserRouter, Routes, Route } from "react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider } from "@/lib/auth";
import { Layout } from "@/components/Layout";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { CheckoutPage } from "@/pages/CheckoutPage";
import { ConfirmationPage } from "@/pages/ConfirmationPage";
import { LoginPage } from "@/pages/LoginPage";
import { ForgotPasswordPage } from "@/pages/ForgotPasswordPage";
import { ResetPasswordPage } from "@/pages/ResetPasswordPage";
import { OrderHistoryPage } from "@/pages/OrderHistoryPage";
import { OrderDetailPage } from "@/pages/OrderDetailPage";
import { RosterPage } from "@/pages/RosterPage";
import { RosterOrderPage } from "@/pages/RosterOrderPage";
import { ReplacementCheckoutPage } from "@/pages/ReplacementCheckoutPage";
import { CompletionCheckoutPage } from "@/pages/CompletionCheckoutPage";
import { StaffAuthProvider } from "@/lib/staff-auth-provider";
import { StaffProtectedRoute } from "@/components/StaffProtectedRoute";
import { StaffLoginPage } from "@/pages/staff/StaffLoginPage";
import { StaffForgotPasswordPage } from "@/pages/staff/StaffForgotPasswordPage";
import { StaffSetPasswordPage } from "@/pages/staff/StaffSetPasswordPage";
import { StaffLayout } from "@/pages/staff/StaffLayout";
import { StaffDashboardPage } from "@/pages/staff/StaffDashboardPage";
import { StaffPaymentsPage } from "@/pages/staff/StaffPaymentsPage";
import { StaffCompaniesPage } from "@/pages/staff/StaffCompaniesPage";
import { StaffCompanyPage } from "@/pages/staff/StaffCompanyPage";
import { StaffSearchPage } from "@/pages/staff/StaffSearchPage";
import { StaffEmployeePage } from "@/pages/staff/StaffEmployeePage";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
    },
  },
});

export function App(): React.ReactElement {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <StaffAuthProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<CheckoutPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/confirmation" element={<ConfirmationPage />} />
              <Route element={<Layout />}>
                <Route element={<ProtectedRoute />}>
                  <Route path="/roster" element={<RosterPage />} />
                  <Route path="/roster/new-order" element={<RosterOrderPage />} />
                  <Route path="/roster/replace" element={<ReplacementCheckoutPage />} />
                  <Route
                    path="/roster/complete/:employeeId"
                    element={<CompletionCheckoutPage />}
                  />
                  <Route path="/orders" element={<OrderHistoryPage />} />
                  <Route path="/orders/:id" element={<OrderDetailPage />} />
                </Route>
              </Route>

              {/* Staff pages render outside the vendor Layout. */}
              <Route path="/staff/login" element={<StaffLoginPage />} />
              <Route path="/staff/forgot-password" element={<StaffForgotPasswordPage />} />
              <Route path="/staff/set-password" element={<StaffSetPasswordPage />} />
              <Route element={<StaffProtectedRoute />}>
                <Route element={<StaffLayout />}>
                  <Route element={<StaffProtectedRoute capability="work_queues" />}>
                    <Route path="/staff" element={<StaffDashboardPage />} />
                    <Route path="/staff/:view" element={<StaffDashboardPage />} />
                  </Route>
                  <Route element={<StaffProtectedRoute capability="view_payments" />}>
                    <Route path="/staff/payments" element={<StaffPaymentsPage />} />
                  </Route>
                  <Route element={<StaffProtectedRoute capability="view_directory" />}>
                    <Route path="/staff/companies" element={<StaffCompaniesPage />} />
                    <Route path="/staff/companies/:id" element={<StaffCompanyPage />} />
                    <Route path="/staff/search" element={<StaffSearchPage />} />
                    <Route path="/staff/employees/:vendorId/:id" element={<StaffEmployeePage />} />
                  </Route>
                </Route>
              </Route>
            </Routes>
          </BrowserRouter>
        </StaffAuthProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
